/**
 * shell-route-registry.ts
 *
 * Registry-driven helpers for MFE ↔ CC navigation sync. States opt in by
 * declaring `data.shellPath` (full shell path with `:param` placeholders, no
 * leading `/{customerId}` segment) and optionally `data.shellAliases` for
 * legacy / CC-public path shapes. This file is the ONLY interpreter of that
 * metadata — event-bus-bridge.init.ts builds the index once at startup and
 * calls matchShellPath() / buildOutgoingInternalRoute() per navigation event.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** A registered shell-route entry in the ShellRouteIndex. */
export interface ShellRouteEntry {
    /** UI-Router state name that owns this pattern. */
    stateName: string;
    /** Ordered `:param` placeholder names from the pattern. */
    paramKeys: string[];
    /** Segment-exact matcher (anchored ^…$), compiled once at index build. */
    regex: RegExp;
}

/** Result of a successful matchShellPath call. */
export interface ShellRouteMatch {
    stateName: string;
    /** Positional `:param` captures plus `customerId` if a leading numeric segment was present. */
    params: Record<string, string>;
}

/** pattern (normalized) → entry. */
export type ShellRouteIndex = Map<string, ShellRouteEntry>;

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/** Matches a single `:paramName` placeholder. */
const PARAM_PLACEHOLDER_RE = /:(\w+)/g;

/**
 * Fresh global regex per call — `g`-flag RegExp is stateful (exec advances
 * lastIndex), so reusing one instance across calls returns wrong results.
 */
function paramPlaceholderRegex (): RegExp {
    return new RegExp(PARAM_PLACEHOLDER_RE.source, 'g');
}

/** '/domains/:domainId/check/:checkId' → ['domainId', 'checkId']. */
function extractParamKeys (pattern: string): string[] {
    const keys: string[] = [];
    let match: RegExpExecArray | null;
    const re = paramPlaceholderRegex();
    while ((match = re.exec(pattern)) !== null) {
        keys.push(match[1]);
    }
    return keys;
}

/** Pattern → segment-exact anchored RegExp; `:param` segments become `([^/]+)`. */
function patternToRegExp (pattern: string): RegExp {
    const regexStr = pattern
        .split('/')
        .map((segment) =>
            segment.startsWith(':') ? '([^/]+)' : segment.replace(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`),
        )
        .join('/');
    return new RegExp(`^${regexStr}$`);
}

/** Normalize a pattern key: ensure leading '/', strip trailing '/' (except root). */
function normalizePattern (pattern: string): string {
    let p = pattern;
    if (!p.startsWith('/')) {
        p = '/' + p;
    }
    if (p.length > 1 && p.endsWith('/')) {
        p = p.slice(0, -1);
    }
    return p;
}

// ---------------------------------------------------------------------------
// 1. interpolateShellPath
// ---------------------------------------------------------------------------

/**
 * Fills `:param` placeholders from `params`. Non-primitive or empty values
 * collapse to an empty segment (double slashes collapsed, trailing slash
 * stripped). Always returns a leading-'/' path. Never throws.
 */
export function interpolateShellPath (shellPath: string, params: Record<string, unknown>): string {
    // First callback arg (full match) is unused — only the capture group matters.
    let result = shellPath.replace(paramPlaceholderRegex(), (...args: string[]) => {
        const key = args[1];
        const value = params[key];
        // Only primitives interpolate — objects/arrays stringify to '[object Object]'.
        if (typeof value !== 'string' && typeof value !== 'number' && typeof value !== 'boolean') {
            return '';
        }
        if (value === '') {
            return '';
        }
        return String(value);
    });

    result = result.replace(/\/+/g, '/');
    if (!result.startsWith('/')) {
        result = '/' + result;
    }
    if (result.length > 1 && result.endsWith('/')) {
        result = result.slice(0, -1);
    }
    return result;
}

// ---------------------------------------------------------------------------
// 2. buildShellPathIndex
// ---------------------------------------------------------------------------

/** Minimal state shape accepted by buildShellPathIndex. */
export interface ShellStateDeclaration {
    name?: string;
    data?: {
        shellPath?: string;
        shellAliases?: string[];
    };
}

/** Depth = number of '.' in the state name ('base.customer.admin.dashboard' → 3). */
function stateDepth (stateName: string): number {
    let count = 0;
    for (const char of stateName) {
        if (char === '.') {
            count++;
        }
    }
    return count;
}

/**
 * Leaf-wins duplicate resolution: the deeper state name wins; equal depth →
 * lexicographically smaller name kept (deterministic). Ensures a leaf child
 * beats a layout ancestor registering the same pattern.
 */
function shouldReplace (existingState: string, candidateState: string): boolean {
    const existingDepth = stateDepth(existingState);
    const candidateDepth = stateDepth(candidateState);
    if (candidateDepth !== existingDepth) {
        return candidateDepth > existingDepth;
    }
    return candidateState < existingState;
}

/**
 * Builds the index from state declarations: registers each state's
 * data.shellPath and each data.shellAliases entry (alias paramKeys derived from
 * the alias pattern itself). Duplicates resolve leaf-wins (see shouldReplace);
 * primary and alias patterns compete in the same namespace.
 */
export function buildShellPathIndex (states: ShellStateDeclaration[]): ShellRouteIndex {
    const index: ShellRouteIndex = new Map<string, ShellRouteEntry>();

    for (const state of states) {
        const stateName = state.name ?? '';
        if (!stateName || !state.data?.shellPath) {
            continue;
        }

        const primaryPattern = normalizePattern(state.data.shellPath);
        const primaryEntry: ShellRouteEntry = {
            stateName,
            paramKeys: extractParamKeys(primaryPattern),
            regex: patternToRegExp(primaryPattern),
        };
        const existingPrimary = index.get(primaryPattern);
        if (existingPrimary === undefined || shouldReplace(existingPrimary.stateName, stateName)) {
            index.set(primaryPattern, primaryEntry);
        }

        for (const alias of state.data.shellAliases ?? []) {
            const aliasPattern = normalizePattern(alias);
            const aliasEntry: ShellRouteEntry = {
                stateName,
                paramKeys: extractParamKeys(aliasPattern),
                regex: patternToRegExp(aliasPattern),
            };
            const existingAlias = index.get(aliasPattern);
            if (existingAlias === undefined || shouldReplace(existingAlias.stateName, stateName)) {
                index.set(aliasPattern, aliasEntry);
            }
        }
    }

    return index;
}

// ---------------------------------------------------------------------------
// 3. matchShellPath
// ---------------------------------------------------------------------------

/** Number of path segments in a pattern. */
function segmentCount (pattern: string): number {
    return pattern.split('/').filter(Boolean).length;
}

/** Number of `:param` placeholders in a pattern. */
function paramCount (pattern: string): number {
    const re = paramPlaceholderRegex();
    let count = 0;
    while (re.exec(pattern) !== null) {
        count++;
    }
    return count;
}

/** Extracts query params from a raw path, returns an object of key-value pairs. */
function extractQueryParams (rawPath: string): Record<string, string> {
    const queryIndex = rawPath.indexOf('?');
    if (queryIndex === -1) {
        return {};
    }
    const queryString = rawPath.slice(queryIndex + 1);
    const params: Record<string, string> = {};
    for (const part of queryString.split('&')) {
        const [key, value] = part.split('=');
        if (key) {
            params[key] = value !== undefined ? decodeURIComponent(value) : '';
        }
    }
    return params;
}

/** Strips query string, '/webgov' prefix; ensures leading '/', strips trailing '/'. */
function normalizeIncomingPath (rawPath: string): string {
    let path = rawPath.split('?')[0];
    if (path.startsWith('/webgov')) {
        path = path.slice('/webgov'.length) || '/';
    }
    if (!path.startsWith('/')) {
        path = '/' + path;
    }
    if (path.length > 1 && path.endsWith('/')) {
        path = path.slice(0, -1);
    }
    return path;
}

/**
 * Strips an optional leading numeric `/{customerId}` segment; returns the
 * remainder + extracted id. Exported so other navigation helpers (LAZY branch
 * URL construction, deep-link bootstrap) can normalise CC-provided paths the
 * same way matchShellPath does.
 */
export function extractCustomerId (path: string): { path: string; customerId?: string } {
    const match = /^\/(\d+)(\/.*|$)/.exec(path);
    if (match === null) {
        return { path };
    }
    let remainder = match[2];
    if (remainder.length === 0) {
        remainder = '/';
    }
    if (!remainder.startsWith('/')) {
        remainder = '/' + remainder;
    }
    return { path: remainder, customerId: match[1] };
}

/** Most-specific first: more segments, then fewer params (literals beat wildcards). */
function sortMostSpecificFirst (index: ShellRouteIndex): Array<[string, ShellRouteEntry]> {
    return [...index.entries()].sort(([patA], [patB]) => {
        const segDiff = segmentCount(patB) - segmentCount(patA);
        if (segDiff !== 0) {
            return segDiff;
        }
        return paramCount(patA) - paramCount(patB);
    });
}

/** Params for a match: extracted customerId + positional `:param` captures. */
function buildMatchParams (
    entry: ShellRouteEntry,
    match: RegExpExecArray,
    customerId: string | undefined,
): Record<string, string> {
    const params: Record<string, string> = {};
    if (customerId !== undefined) {
        params['customerId'] = customerId;
    }
    entry.paramKeys.forEach((key, idx) => {
        const captured = match[idx + 1];
        if (captured !== undefined) {
            params[key] = captured;
        }
    });
    return params;
}

/**
 * Matches `rawPath` against the index (after normalizeIncomingPath +
 * extractCustomerId). Case-sensitive, segment-exact, most-specific first.
 * Query parameters from the raw path are preserved in the returned params.
 * Returns null when nothing matches.
 */
export function matchShellPath (index: ShellRouteIndex, rawPath: string): ShellRouteMatch | null {
    const normalized = normalizeIncomingPath(rawPath);
    const { path, customerId } = extractCustomerId(normalized);
    const queryParams = extractQueryParams(rawPath);

    for (const [, entry] of sortMostSpecificFirst(index)) {
        const match = entry.regex.exec(path);
        if (match === null) {
            continue;
        }
        const pathParams = buildMatchParams(entry, match, customerId);
        return { stateName: entry.stateName, params: { ...queryParams, ...pathParams } };
    }

    return null;
}

// ---------------------------------------------------------------------------
// 4. buildOutgoingInternalRoute
// ---------------------------------------------------------------------------

/** Minimal state shape accepted by buildOutgoingInternalRoute. */
export interface OutgoingStateDeclaration {
    name?: string;
    data?: {
        shellPath?: string;
    };
}

/**
 * Builds the internalRoute to publish. Returns null (suppress) when the state
 * has no data.shellPath (opt-in) or the path starts with '/error'.
 *
 * Query string: appends params that are NOT path placeholders, NOT customerId,
 * and have primitive (string/number/boolean) non-null values. Empty strings are
 * kept (search= is intentional). Insertion order.
 */
export function buildOutgoingInternalRoute (
    state: OutgoingStateDeclaration | null | undefined,
    params: Record<string, unknown>,
): string | null {
    const shellPath = state?.data?.shellPath;
    if (!shellPath) {
        return null;
    }

    const interpolated = interpolateShellPath(shellPath, params);
    if (interpolated.startsWith('/error')) {
        return null;
    }

    // Placeholder names used in shellPath are excluded from the query string.
    const placeholderSet = new Set<string>();
    const placeholderRe = paramPlaceholderRegex();
    let placeholderMatch: RegExpExecArray | null;
    while ((placeholderMatch = placeholderRe.exec(shellPath)) !== null) {
        placeholderSet.add(placeholderMatch[1]);
    }

    const queryParts: string[] = [];
    for (const key of Object.keys(params)) {
        if (key === 'customerId' || placeholderSet.has(key)) {
            continue;
        }
        const value = params[key];
        // Only primitives — objects/arrays stringify to '[object Object]'.
        if (typeof value !== 'string' && typeof value !== 'number' && typeof value !== 'boolean') {
            continue;
        }
        queryParts.push(`${key}=${encodeURIComponent(String(value))}`);
    }

    if (queryParts.length === 0) {
        return interpolated;
    }
    return `${interpolated}?${queryParts.join('&')}`;
}
